import { useId } from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { SplitProtected } from '@buffer-mono/features'
import { useLocalStorage, VisuallyHidden } from '@buffer-mono/popcorn'

import { ALL_CHANNELS_TIMEZONE_STORAGE_KEY } from '~publish/components/AllChannelsTimeZoneSelect'
import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import { TimezoneProvider } from '~publish/hooks/useTimezone'

import { CommentsSection } from './CommentsSection'
import { ContentCarousel } from './ContentCarousel'
import { DraftsSection } from './DraftsSection'
import { AllSetMessage, FirstSteps } from './FirstSteps'
import { GetInspiredCarousel } from './GetInspiredCarousel'
import { GreetingHeader } from './GreetingHeader'
import { HomeLayout } from './HomeLayout'
import { HomeSlotSkeleton } from './HomeSlotSkeleton'
import { HomePageProvider, useHomePage } from './HomePageProvider'
import { InsightsSummaryModule } from './InsightsSummaryModule'
import { ScorecardPanel } from './ScorecardPanel'
import { SyncStatus } from './SyncStatus'
import { UpNextSection } from './UpNextSection'
import { useHomeSlotPhase } from './useHomeSlotPhase'

import styles from './HomeLayout/HomeLayout.module.css'

import type { JSX } from 'react'

const HomePage = (): JSX.Element => {
  const [timeZone] = useLocalStorage(
    ALL_CHANNELS_TIMEZONE_STORAGE_KEY,
    CURRENT_TIME_ZONE,
  )

  return (
    <TimezoneProvider timeZone={timeZone}>
      <HomePageProvider>
        <HomePageContent />
      </HomePageProvider>
    </TimezoneProvider>
  )
}

const slotMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.7, ease: 'easeOut' },
} as const

function HomePageContent(): JSX.Element {
  const { userState } = useHomePage()
  const slotPhase = useHomeSlotPhase()
  const scorecardHeadingId = useId()
  // Accounts past the 30-day onboarding window always see Insights —
  // they bypass the FirstSteps / AllSet phases entirely and don't need
  // to wait on the checklist query that drives the slot phase (CORE-5281).
  const effectivePhase = userState === 'default' ? 'insights' : slotPhase

  return (
    <HomeLayout>
      <GreetingHeader>
        <GreetingHeader.Group>
          <GreetingHeader.Title />
          <GreetingHeader.SubTitle />
        </GreetingHeader.Group>
        <GreetingHeader.Actions>
          <FeedbackWidget id="home-greeting-feedback" align="end" />
          <SyncStatus />
        </GreetingHeader.Actions>
      </GreetingHeader>

      <section
        aria-labelledby={scorecardHeadingId}
        className={styles.scorecardRow}
      >
        <VisuallyHidden as="h2" id={scorecardHeadingId}>
          Your scorecard
        </VisuallyHidden>
        <ScorecardPanel />
      </section>

      <AnimatePresence mode="popLayout" initial={false}>
        {effectivePhase === 'loading' && (
          <m.section key="loading" className={styles.insights} {...slotMotion}>
            <HomeSlotSkeleton />
          </m.section>
        )}
        {effectivePhase === 'firstSteps' && (
          <m.section
            key="firstSteps"
            className={styles.firstSteps}
            {...slotMotion}
          >
            <FirstSteps />
          </m.section>
        )}
        {effectivePhase === 'allSet' && (
          <m.section key="allSet" className={styles.allSet} {...slotMotion}>
            <AllSetMessage />
          </m.section>
        )}
        {effectivePhase === 'insights' && (
          <m.section key="insights" className={styles.insights} {...slotMotion}>
            <InsightsSummaryModule />
          </m.section>
        )}
      </AnimatePresence>

      <section className={styles.drafts}>
        <DraftsSection />
      </section>

      <section className={styles.mainContent}>
        <UpNextSection />
      </section>

      <aside className={styles.rightRail}>
        <CommentsSection />
      </aside>

      <section className={styles.getInspired}>
        <GetInspiredCarousel />
      </section>

      <SplitProtected name="home-blog-carousel">
        <section className={styles.fromTheBlog}>
          <ContentCarousel />
        </section>
      </SplitProtected>
    </HomeLayout>
  )
}

export default HomePage
